import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { WorkGroupAdminQuotasPanel } from '@/features/workgroups/components/work-group-admin-quotas-panel';
import { ProjectWorkGroupPanel } from '@/features/workgroups/components/project-workgroup-panel';
import { WorkGroupCard } from '@/features/workgroups/components/work-group-card';
import { WorkGroupQuotaBadge } from '@/features/workgroups/components/work-group-quota-badge';
import { WorkGroupEmptyState } from '@/features/workgroups/components/work-group-empty-state';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useCreateStandaloneWorkGroup,
  useMyPrivateWorkGroup,
  useMyWorkGroupQuota,
  useWorkGroups,
} from '@/hooks/use-workgroups';

export function WorkGroupsPage() {
  const { hasCapability } = usePermissions();
  const { data: groups, isLoading, error } = useWorkGroups();
  const { data: quota } = useMyWorkGroupQuota();
  const { data: privateWg, error: privateError } = useMyPrivateWorkGroup();
  const createMutation = useCreateStandaloneWorkGroup();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const canCreate = hasCapability('workgroups.create');
  const canViewProjectWorkGroups = hasCapability('workgroups.view');
  const canManageQuotas = hasCapability('workgroups.quota.manage');
  const displayGroups =
    groups?.filter((g) => (privateWg && !privateError ? g.id !== privateWg.id : true)) ?? [];
  const showGlobalEmpty =
    !isLoading &&
    !error &&
    displayGroups.length === 0 &&
    !(privateWg && !privateError);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">WeWorkGroups</h1>
          <p className="text-sm text-muted-foreground">Work groups you can access.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {quota ? <WorkGroupQuotaBadge quota={quota} /> : null}
          {canCreate ? (
            <Dialog
              open={createOpen}
              onOpenChange={(open) => {
                setCreateOpen(open);
                if (!open) {
                  setNewName('');
                }
              }}
            >
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-2 h-4 w-4" aria-hidden />
                  New standalone
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create standalone work group</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 py-2">
                  <Label htmlFor="wg-name">Name</Label>
                  <Input
                    id="wg-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Team files"
                    autoComplete="off"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    disabled={!newName.trim() || createMutation.isPending}
                    onClick={() => {
                      createMutation.mutate(
                        { name: newName.trim() },
                        {
                          onSuccess: () => {
                            setCreateOpen(false);
                            setNewName('');
                          },
                        },
                      );
                    }}
                  >
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
        </div>
      </div>

      {canManageQuotas ? <WorkGroupAdminQuotasPanel /> : null}

      {canViewProjectWorkGroups ? <ProjectWorkGroupPanel /> : null}

      {privateWg && !privateError ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-foreground">Private</h2>
          <WorkGroupCard workGroup={privateWg} />
        </section>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading work groups…</p>
      ) : error ? (
        <p className="text-sm text-destructive">Could not load work groups.</p>
      ) : showGlobalEmpty ? (
        <WorkGroupEmptyState
          title="No work groups yet"
          description="Create a standalone work group or open a project work group from a project when available."
        />
      ) : displayGroups.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {displayGroups.map((wg) => (
            <WorkGroupCard key={wg.id} workGroup={wg} />
          ))}
        </div>
      ) : privateWg && !privateError ? (
        <p className="text-sm text-muted-foreground">No other work groups in your list.</p>
      ) : null}

      <p className="text-xs text-muted-foreground">
        <Button variant="link" className="h-auto p-0 text-xs" asChild>
          <Link to="/start">Back to module selection</Link>
        </Button>
      </p>
    </div>
  );
}
